import React, { useState, useRef, useEffect } from "react";
import { Search, ChevronDown, X } from "lucide-react";

const SearchableSupplierDropdown = ({ suppliers, value, onChange, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const listContainerRef = useRef(null);

  // Find selected supplier name
  const selectedSupplier = suppliers.find((s) => s.id === value);
  const displayText = selectedSupplier
    ? selectedSupplier.name
    : "All Suppliers";

  // Filter suppliers based on search
  const filteredSuppliers = suppliers.filter((supplier) =>
    supplier.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // All options including "All Suppliers"
  const allOptions = [{ id: "", name: "All Suppliers" }, ...filteredSuppliers];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset selected index when dropdown opens/closes or search changes
  useEffect(() => {
    if (!isOpen) {
      setSelectedIndex(-1);
    }
  }, [isOpen]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && listContainerRef.current) {
      const selectedElement = listContainerRef.current.querySelector(
        `#supplier-option-${selectedIndex}`
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [selectedIndex]);

  const handleSelect = (supplierId) => {
    onChange(supplierId);
    setIsOpen(false);
    setSearchTerm("");
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!allOptions.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev < allOptions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < allOptions.length) {
        handleSelect(allOptions[selectedIndex].id);
      }
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
    setSearchTerm("");
  };

  return (
    <div ref={dropdownRef} className="relative">
      {label && (
        <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
          {label}
        </label>
      )}

      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-left flex items-center justify-between"
      >
        <span className={value ? "text-gray-900" : "text-gray-500"}>
          {displayText}
        </span>
        <div className="flex items-center gap-1">
          {value && (
            <X
              size={16}
              className="text-gray-400 hover:text-gray-600"
              onClick={handleClear}
            />
          )}
          <ChevronDown
            size={16}
            className={`text-gray-400 transition-transform ${isOpen ? "transform rotate-180" : ""
              }`}
          />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search suppliers..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          {/* Options List */}
          <div className="overflow-y-auto max-h-60" ref={listContainerRef}>
            {/* All Suppliers Option */}
            <button
              type="button"
              id="supplier-option-0"
              onClick={() => handleSelect("")}
              className={`w-full text-left px-4 py-2 text-sm transition-colors ${selectedIndex === 0
                  ? "bg-blue-200"
                  : !value
                    ? "bg-blue-50 text-blue-700 font-medium"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
            >
              All Suppliers
            </button>

            {/* Filtered Suppliers */}
            {filteredSuppliers.length > 0 ? (
              filteredSuppliers.map((supplier, idx) => (
                <button
                  key={supplier.id}
                  type="button"
                  id={`supplier-option-${idx + 1}`}
                  onClick={() => handleSelect(supplier.id)}
                  className={`w-full text-left px-4 py-2 text-sm transition-colors ${selectedIndex === idx + 1
                      ? "bg-blue-200"
                      : value === supplier.id
                        ? "bg-blue-50 text-blue-700 font-medium"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                >
                  {supplier.name}
                </button>
              ))
            ) : (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                No suppliers found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSupplierDropdown;
